import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { CircularProgressBar } from '@sorare/core/src/atoms/loader/CircularProgressBar';
import { BodyS, LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DumbTask } from '#src/components/missions/DumbTask/index.tsx';

import type { GameWeeklyTask_taskInterface } from './__generated__/index.graphql.ts';

const Img = styled.img`
  width: var(--triple-unit);
  @media ${laptopAndAbove} {
    width: var(--quadruple-unit);
  }
`;

const Footer = styled(SBHorizontal).attrs({ gap: 2 })`
  align-items: flex-end;

  &:not(.featured) {
    @media ${laptopAndAbove} {
      align-items: center;
      justify-content: flex-start;
      & > :last-child {
        padding-left: var(--double-unit);
        border-left: 1px solid var(--c-nd-150);
      }
    }
  }
`;
const UnbreakableLabelS = styled(LabelS)`
  white-space: nowrap;
`;
const RewardWrapper = styled.div`
  padding-bottom: var(--half-unit);
`;

type Props = {
  gameWeeklyTask: GameWeeklyTask_taskInterface;
  featured?: boolean;
  borderColor?: ComponentProps<typeof DumbTask>['borderColor'];
};
export const GameWeeklyTask = ({
  gameWeeklyTask,
  featured,
  borderColor,
}: Props) => {
  const {
    templateIconUrl,
    iconUrl,
    title,
    description,
    rewardConfigs,
    progress,
    target,
  } = gameWeeklyTask;
  const reward = rewardConfigs.at(0);

  return (
    <DumbTask
      featured={featured}
      borderColor={borderColor}
      icon={!!templateIconUrl && <Img src={templateIconUrl} alt="" />}
      title={
        <Horizontal>
          <LabelL bold>{title}</LabelL>
          {!!iconUrl && <img width={20} src={iconUrl} alt="" />}
        </Horizontal>
      }
      description={
        <BodyS color="var(--c-nd-600)">
          <Ellipsis visibleLines={2}>{description}</Ellipsis>
        </BodyS>
      }
      footer={
        <Footer className={classNames({ featured })}>
          <CircularProgressBar value={progress} max={target} size={68}>
            <Vertical center gap={0.5}>
              <Horizontal gap={0.5}>
                <UnbreakableLabelS color="var(--c-nd-600)" bold>
                  <FormattedMessage
                    defaultMessage="<white>{progress}</white> / {target}"
                    values={{
                      progress,
                      target,
                      white: content => (
                        <span style={{ color: 'var(--c-white)' }}>
                          {content}
                        </span>
                      ),
                    }}
                  />
                </UnbreakableLabelS>
              </Horizontal>
            </Vertical>
          </CircularProgressBar>
          {featured ? (
            <RewardWrapper>
              {!!reward && (
                <AnyRewardConfig
                  anyRewardConfig={reward}
                  variant={featured ? Variant.INLINE : Variant.CHIP}
                />
              )}
            </RewardWrapper>
          ) : (
            !!reward && (
              <AnyRewardConfig
                anyRewardConfig={reward}
                variant={featured ? Variant.INLINE : Variant.CHIP}
              />
            )
          )}
        </Footer>
      }
    />
  );
};

GameWeeklyTask.fragments = {
  taskInterface: gql`
    fragment GameWeeklyTask_taskInterface on TaskInterface {
      id
      color
      iconUrl
      templateIconUrl
      title
      description
      progress
      target
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<GameWeeklyTask_taskInterface>,
};
